// Ambient sea traffic plan (render-only, docs/sea-design.md 5.1): closed
// routes, ship and gull placement. Pure and seeded — the same board gives the
// same plan on every client, nothing touches the sim, saves or hashes.
//
//   Open sea   one or two closed loops around the whole board (rounded
//              rectangles through the ocean ring), ships on both.
//   Rim        short stadium loops hugging the board edge inside the existing
//              2-thick ocean + coast band (coastal traffic).
//   Landlocked no sea, so no plan.
//
// Every sample point of a route must sit on a sea tile, else the route is
// dropped ("they only exist where there is sea").

import type { SeaMode } from './seaFrame.js';
import { axialXZ, boundaryEdges, key, outwardRings, parseKey, worldToAxial } from './seaTiles.js';

export const MAX_SHIPS = 24;
/**
 * Gulls are atmosphere, not scenery: 64 of them along one shoreline read as
 * debris scattered on the sand (the real-GPU pass, 2026-10-22). A couple of
 * dozen, high enough to fly clear of the beach, reads as a gull flock.
 */
export const MAX_GULLS = 24;
/** Sail tints (the hull is never tinted). Index stored per ship. */
export const SHIP_PALETTE: readonly string[] = [
  '#fbf6e8', // undyed canvas
  '#f58a7a', // red
  '#f7d070', // ochre
  '#86b8f0', // blue
  '#92d3a0', // green
  '#c4a0e4', // purple
];

/**
 * Pirate colours: tarred canvas and old blood, nothing pastel. Pirates keep to
 * the open-sea loops (`Route.kind === 'outer'`), away from the coastal lanes the
 * merchants work, so a fleet reads as two kinds of traffic rather than one.
 */
export const PIRATE_PALETTE: readonly string[] = [
  '#0e0e10', // black
  '#141317', // tar
  '#1b1a1e', // sooty canvas
  '#241a18', // old blood, nearly black
];

/** Merchant or pirate — cosmetic only; the sim has no opinion. */
export type ShipRole = 'merchant' | 'pirate';

export interface Route {
  readonly xs: Float32Array;
  readonly zs: Float32Array;
  /** Water surface Y at each point. */
  readonly ys: Float32Array;
  /** Cumulative arc length at each point; cum[n] is the full loop length. */
  readonly cum: Float32Array;
  readonly length: number;
  /** Coastal/trade lane, or the outer loop pirates work. */
  readonly kind: 'lane' | 'outer';
}

export interface Ship {
  readonly route: number;
  /** Start arc position (world units along the route). */
  readonly start: number;
  /** Signed speed, world units per second. */
  readonly speed: number;
  /** Rig: 1 = single mast, 2 = brig, 3 = three-master (galleon). */
  readonly variant: 1 | 2 | 3;
  /** Merchant (trade lanes) or pirate (open sea). */
  readonly role: ShipRole;
  /** Hull size multiplier (0.78 small .. 1.22 large), applied as instance scale. */
  readonly size: number;
  /** Index into the role's palette. */
  readonly tint: number;
  /** 0..1 per-ship seed (bob phase etc). */
  readonly phase: number;
}

export interface Gull {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly radius: number;
  /** Signed angular speed, rad/s. */
  readonly omega: number;
  readonly angle0: number;
  readonly phase: number;
}

export interface TrafficPlan {
  readonly routes: Route[];
  readonly ships: Ship[];
  readonly gulls: Gull[];
}

export const EMPTY_PLAN: TrafficPlan = { routes: [], ships: [], gulls: [] };

export interface TrafficInput {
  readonly mode: SeaMode;
  readonly seed: number;
  /** key -> biome of the rendered board (wall already applied for landlocked). */
  readonly board: ReadonlyMap<string, string>;
  /** Ocean rings rendered beyond the board (Open sea). */
  readonly extraRings: number;
  /** Water surface Y at a world point. */
  readonly waterY: (x: number, z: number) => number;
}

/** mulberry32 — tiny seeded PRNG, no Math.random anywhere. */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const WATER = new Set(['ocean', 'coast']);

/** Sea tile keys: the board's ocean/coast plus the extra ocean rings. */
export function seaTileKeys(board: ReadonlyMap<string, string>, extraRings: number): Set<string> {
  const sea = new Set<string>();
  for (const [k, b] of board) if (WATER.has(b)) sea.add(k);
  if (extraRings > 0) {
    for (const t of outwardRings(new Set(board.keys()), extraRings)) sea.add(key(t.q, t.r));
  }
  return sea;
}

/** Rounded rectangle as a closed point loop, clockwise in XZ. */
export function roundedRectLoop(
  cx: number,
  cz: number,
  hx: number,
  hz: number,
  rad: number,
  yaw = 0,
  arcSteps = 6,
  sideSteps = 10,
): Array<[number, number]> {
  const r = Math.max(0.01, Math.min(rad, hx, hz));
  const pts: Array<[number, number]> = [];
  const corners: Array<[number, number, number]> = [
    [hx - r, -hz + r, -Math.PI / 2],
    [hx - r, hz - r, 0],
    [-hx + r, hz - r, Math.PI / 2],
    [-hx + r, -hz + r, Math.PI],
  ];
  const side = (x0: number, z0: number, x1: number, z1: number) => {
    for (let i = 1; i < sideSteps; i++) {
      const t = i / sideSteps;
      pts.push([x0 + (x1 - x0) * t, z0 + (z1 - z0) * t]);
    }
  };
  corners.forEach(([ccx, ccz, a0], i) => {
    for (let s = 0; s <= arcSteps; s++) {
      const a = a0 + ((Math.PI / 2) * s) / arcSteps;
      pts.push([ccx + Math.cos(a) * r, ccz + Math.sin(a) * r]);
    }
    const n = corners[(i + 1) % 4]!;
    const ex = n[0] + Math.cos(n[2]) * r;
    const ez = n[1] + Math.sin(n[2]) * r;
    const lx = ccx + Math.cos(a0 + Math.PI / 2) * r;
    const lz = ccz + Math.sin(a0 + Math.PI / 2) * r;
    side(lx, lz, ex, ez);
  });
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  return pts.map(([x, z]) => [cx + x * c - z * s, cz + x * s + z * c]);
}

function buildRoute(
  pts: ReadonlyArray<readonly [number, number]>,
  waterY: (x: number, z: number) => number,
  kind: 'lane' | 'outer' = 'lane',
): Route {
  const n = pts.length;
  const xs = new Float32Array(n);
  const zs = new Float32Array(n);
  const ys = new Float32Array(n);
  const cum = new Float32Array(n + 1);
  for (let i = 0; i < n; i++) {
    const [x, z] = pts[i]!;
    xs[i] = x;
    zs[i] = z;
    ys[i] = waterY(x, z);
    const [nx, nz] = pts[(i + 1) % n]!;
    cum[i + 1] = cum[i]! + Math.hypot(nx - x, nz - z);
  }
  return { xs, zs, ys, cum, length: cum[n]!, kind };
}

/** True when every point lies on a sea tile. */
function routeIsSea(
  pts: ReadonlyArray<readonly [number, number]>,
  sea: ReadonlySet<string>,
): boolean {
  return pts.every(([x, z]) => {
    const [q, r] = worldToAxial(x, z);
    return sea.has(key(q, r));
  });
}

export interface SamplePoint {
  x: number;
  y: number;
  z: number;
  /** Unit heading. */
  dx: number;
  dz: number;
}

/** Point + heading at arc position `s` (wraps; negative ok). Allocation-free. */
export function sampleRoute(route: Route, s: number, out: SamplePoint): SamplePoint {
  const n = route.xs.length;
  const L = route.length;
  const u = ((s % L) + L) % L;
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (route.cum[mid]! <= u) lo = mid;
    else hi = mid - 1;
  }
  const j = (lo + 1) % n;
  const seg = route.cum[lo + 1]! - route.cum[lo]!;
  const t = seg > 1e-6 ? (u - route.cum[lo]!) / seg : 0;
  const x0 = route.xs[lo]!;
  const z0 = route.zs[lo]!;
  const ex = route.xs[j]! - x0;
  const ez = route.zs[j]! - z0;
  const len = Math.hypot(ex, ez) || 1;
  out.x = x0 + ex * t;
  out.z = z0 + ez * t;
  out.y = route.ys[lo]! + (route.ys[j]! - route.ys[lo]!) * t;
  out.dx = ex / len;
  out.dz = ez / len;
  return out;
}

function bounds(board: ReadonlyMap<string, string>) {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const k of board.keys()) {
    const [q, r] = parseKey(k);
    const [x, z] = axialXZ(q, r);
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
  }
  return { minX, maxX, minZ, maxZ };
}

// One ship per this much route length. The sea read as busy — a fleet in every
// lane — so the spacing roughly doubled (owner's note, 2026-10-22).
const SHIP_SPACING = 26;
const ZIG = 1.5; // world thickness of one ring row

function openLoops(
  input: TrafficInput,
  sea: ReadonlySet<string>,
): Array<{ pts: Array<[number, number]>; kind: 'lane' | 'outer' }> {
  const w = input.extraRings;
  const { minX, maxX, minZ, maxZ } = bounds(input.board);
  const lanes = w >= 2 ? [0.3, 0.7] : [0.5];
  const loops: Array<{ pts: Array<[number, number]>; kind: 'lane' | 'outer' }> = [];
  for (const f of lanes) {
    // The outermost lane is pirate water; the inner one is the trade lane.
    const kind: 'lane' | 'outer' =
      f === lanes[lanes.length - 1] && lanes.length > 1 ? 'outer' : 'lane';
    const off = f * w * ZIG + 0.3;
    const pts = roundedRectLoop(
      (minX + maxX) / 2,
      (minZ + maxZ) / 2,
      (maxX - minX) / 2 + off,
      (maxZ - minZ) / 2 + off,
      Math.min(2.5, off + 1),
    );
    if (routeIsSea(pts, sea)) loops.push({ pts, kind });
  }
  return loops;
}

const MAX_RIM_LOOPS = 6;
const RIM_HALF_LEN = 2.3;
const RIM_HALF_WID = 0.4;
const RIM_MIN_SPACING = 7;

function rimLoops(
  input: TrafficInput,
  sea: ReadonlySet<string>,
): Array<{ pts: Array<[number, number]>; kind: 'lane' | 'outer' }> {
  const edges = boundaryEdges(new Set(input.board.keys()));
  const rand = rng(input.seed ^ 0x51ed);
  // Seeded Fisher-Yates over a copy: deterministic candidate order.
  const order = edges.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  const loops: Array<{ pts: Array<[number, number]>; kind: 'lane' | 'outer' }> = [];
  const taken: Array<[number, number]> = [];
  for (const idx of order) {
    if (loops.length >= MAX_RIM_LOOPS) break;
    const e = edges[idx]!;
    if (taken.some(([x, z]) => Math.hypot(x - e.x, z - e.z) < RIM_MIN_SPACING)) continue;
    const cx = e.x - e.nx * 0.9;
    const cz = e.z - e.nz * 0.9;
    // Long axis along the edge tangent (-nz, nx).
    const yaw = Math.atan2(e.nx, -e.nz);
    const pts = roundedRectLoop(cx, cz, RIM_HALF_LEN, RIM_HALF_WID, RIM_HALF_WID, yaw, 4, 8);
    if (!routeIsSea(pts, sea)) continue;
    loops.push({ pts, kind: 'lane' });
    taken.push([e.x, e.z]);
  }
  return loops;
}

/** 20% sloops, 40% brigs, 40% three-masters. */
function pickVariant(rand: () => number): 1 | 2 | 3 {
  const r = rand();
  return r < 0.2 ? 1 : r < 0.6 ? 2 : 3;
}

/** Small, medium or large hull — orthogonal to the rig. */
function pickSize(rand: () => number): number {
  const r = rand();
  return r < 0.3 ? 0.78 : r < 0.78 ? 1.0 : 1.22;
}

/** Pirates raid in brigs and galleons — rarely a lone sloop. */
function pickPirateVariant(rand: () => number): 1 | 2 | 3 {
  const r = rand();
  return r < 0.15 ? 1 : r < 0.55 ? 2 : 3;
}

function placeShips(routes: Route[], rand: () => number, perLoopMin: number): Ship[] {
  const ships: Ship[] = [];
  routes.forEach((route, ri) => {
    const want = Math.max(perLoopMin, Math.round(route.length / SHIP_SPACING));
    const dir = ri % 2 === 0 ? 1 : -1;
    const base = rand();
    for (let k = 0; k < want && ships.length < MAX_SHIPS; k++) {
      // Pirates work the outer loops; a few run the lanes, as raiders do.
      const role: ShipRole =
        rand() < (route.kind === 'outer' ? 0.55 : 0.12) ? 'pirate' : 'merchant';
      ships.push({
        route: ri,
        start: ((base + k / want) % 1) * route.length + (rand() - 0.5) * 1.2,
        // Slow and stately: ~0.03-0.05 units/s (a tenth of the old 0.32-0.52),
        // so a ship reads as sailing rather than skating (owner's note).
        speed: dir * (0.032 + rand() * 0.02),
        // Mostly two- and three-masters: a fleet of identical single-mast cogs
        // looked generic (owner's note, 2026-10-22).
        // Pirates favour the big hulls; merchants the smaller traders.
        variant: role === 'pirate' ? pickPirateVariant(rand) : pickVariant(rand),
        // Size is independent of rig (the Pirate Quest model treats it as a pure
        // scale): a sloop can be a big sloop, a galleon a small one.
        size: pickSize(rand),
        role,
        tint: Math.floor(
          rand() * (role === 'pirate' ? PIRATE_PALETTE.length : SHIP_PALETTE.length),
        ),
        phase: rand(),
      });
    }
  });
  return ships;
}

function placeGulls(input: TrafficInput, sea: ReadonlySet<string>, rand: () => number): Gull[] {
  // Flock centres prefer coast tiles (gulls hang about the shore).
  const coast = [...sea].filter((k) => input.board.get(k) === 'coast').sort();
  const any = [...sea].sort();
  const pool = coast.length >= 4 ? coast : any;
  if (pool.length === 0) return [];
  const total = Math.min(MAX_GULLS, Math.floor(sea.size / 3));
  const gulls: Gull[] = [];
  const FLOCK = 4;
  for (let f = 0; gulls.length < total; f++) {
    const [q, r] = parseKey(pool[Math.floor(rand() * pool.length)]!);
    const [cx, cz] = axialXZ(q, r);
    // Well clear of the water: a gull at half a unit up sat level with the
    // beach and read as a dark nail lying in the sand instead of a bird in the
    // air (the real-GPU pass, 2026-10-22).
    const cy = input.waterY(cx, cz) + 1.9 + rand() * 1.4;
    const dir = rand() < 0.5 ? 1 : -1;
    for (let k = 0; k < FLOCK && gulls.length < total; k++) {
      gulls.push({
        x: cx,
        y: cy + rand() * 0.25,
        z: cz,
        radius: 0.35 + rand() * 0.75,
        omega: dir * (0.35 + rand() * 0.3),
        angle0: rand() * Math.PI * 2,
        phase: rand(),
      });
    }
    if (f > MAX_GULLS) break; // safety: pool always yields
  }
  return gulls;
}

/** The whole plan. Landlocked / no sea => EMPTY_PLAN. */
export function planTraffic(input: TrafficInput): TrafficPlan {
  if (input.mode === 'landlocked' || input.board.size === 0) return EMPTY_PLAN;
  const sea = seaTileKeys(input.board, input.extraRings);
  if (sea.size === 0) return EMPTY_PLAN;
  const rand = rng(input.seed);
  const pointLoops =
    input.mode === 'open' && input.extraRings > 0 ? openLoops(input, sea) : rimLoops(input, sea);
  const routes = pointLoops.map((l) => buildRoute(l.pts, input.waterY, l.kind));
  const ships = placeShips(routes, rand, input.mode === 'open' ? 2 : 1);
  return { routes, ships, gulls: placeGulls(input, sea, rand) };
}
